import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, User, FileText, Stethoscope, Plane, AlertOctagon, Clock, Edit, IdCard } from 'lucide-react';
import { Button } from '../../components/ui/button';
import api from '../../services/api';
import { toast } from 'sonner';

export function EmployeeProfile() {
  const { id } = useParams();
  const nav = useNavigate();
  const [emp, setEmp] = useState(null);
  const [tab, setTab] = useState('overview');
  const [loading, setLoading] = useState(true);

  // Sub-resource lists
  const [docs, setDocs] = useState([]);
  const [exams, setExams] = useState([]);
  const [vacations, setVacations] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [occurrences, setOccurrences] = useState([]);
  const [timeline, setTimeline] = useState([]);

  useEffect(() => {
    loadData();
  }, [id]);

  useEffect(() => {
    if (tab !== 'overview') loadSubResource(tab);
  }, [tab]);

  const loadData = async () => {
    try {
      const { data } = await api.get(`/employees/${id}`);
      setEmp(data);
    } catch (err) {
      toast.error('Erro ao carregar perfil.');
    } finally {
      setLoading(false);
    }
  };

  const loadSubResource = async (t) => {
    try {
      if (t === 'docs') { const r = await api.get(`/employees/${id}/documents`); setDocs(r.data); }
      if (t === 'exams') { const r = await api.get(`/employees/${id}/exams`); setExams(r.data); }
      if (t === 'vacations') { const r = await api.get(`/employees/${id}/vacations`); setVacations(r.data); }
      if (t === 'leaves') { const r = await api.get(`/employees/${id}/leaves`); setLeaves(r.data); }
      if (t === 'occurrences') { const r = await api.get(`/employees/${id}/occurrences`); setOccurrences(r.data); }
      if (t === 'timeline') { const r = await api.get(`/employees/${id}/timeline`); setTimeline(r.data); }
    } catch(err) {
      toast.error('Erro ao carregar ' + t);
    }
  };

  if (loading) return <div className="p-8 text-center text-slate-500">Carregando perfil...</div>;
  if (!emp) return <div className="p-8 text-center text-red-500">Funcionário não encontrado.</div>;

  const tabs = [
    { id: 'overview', icon: User, label: 'Visão Geral' },
    { id: 'docs', icon: FileText, label: 'Documentos' },
    { id: 'exams', icon: Stethoscope, label: 'Exames' },
    { id: 'vacations', icon: Plane, label: 'Férias' },
    { id: 'leaves', icon: AlertOctagon, label: 'Afastamentos' },
    { id: 'occurrences', icon: AlertOctagon, label: 'Ocorrências' },
    { id: 'timeline', icon: Clock, label: 'Histórico' },
  ];

  return (
    <div className="p-6 max-w-[1400px] mx-auto space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl border p-6 flex flex-col md:flex-row items-center gap-6 relative">
        <Button variant="ghost" className="absolute top-4 left-4" onClick={() => nav('/rh/funcionarios')}><ArrowLeft className="w-5 h-5"/></Button>
        <div className="w-24 h-24 rounded-full bg-slate-200 flex items-center justify-center text-3xl font-bold text-slate-400 mt-8 md:mt-0 ml-12 overflow-hidden">
          {emp.photo_url ? (
            <img src={emp.photo_url} alt={emp.name} className="w-full h-full object-cover" />
          ) : (
            emp.name ? emp.name.charAt(0).toUpperCase() : '?'
          )}
        </div>
        <div className="flex-1 text-center md:text-left">
          <h1 className="text-2xl font-bold text-slate-800">{emp.name || 'Sem nome'}</h1>
          <p className="text-slate-500">{emp.professional?.role || 'Sem cargo'} • {emp.professional?.department || 'Sem setor'}</p>
          <div className="flex flex-wrap justify-center md:justify-start gap-4 mt-3">
            <span className="px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-sm font-medium">Matrícula: {emp.professional?.matricula || '-'}</span>
            <span className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium">Status: {emp.status || 'Ativo'}</span>
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => nav(`/rh/funcionarios/${id}/editar`)} variant="outline"><Edit className="w-4 h-4 mr-2"/>Editar</Button>
          <Button onClick={() => {
            if(window.confirm('Confirma desligamento deste funcionário?')) {
              api.post(`/employees/${id}/terminate`, { reason: 'Desligamento manual' }).then(() => { toast.success('Desligado.'); loadData(); });
            }
          }} variant="destructive">Desligar</Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex overflow-x-auto border-b">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} className={`px-4 py-3 flex items-center gap-2 font-medium whitespace-nowrap border-b-2 transition-colors ${tab === t.id ? 'border-brand text-brand' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="bg-white rounded-xl border p-6 min-h-[400px]">
        {tab === 'overview' && (
           <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
             <div>
               <h3 className="font-semibold text-lg border-b pb-2 mb-4">Dados Básicos</h3>
               <p className="text-sm mb-1"><span className="text-slate-500 w-24 inline-block">CPF:</span> {emp.cpf}</p>
               <p className="text-sm mb-1"><span className="text-slate-500 w-24 inline-block">RG:</span> {emp.rg}</p>
               <p className="text-sm mb-1"><span className="text-slate-500 w-24 inline-block">Nascimento:</span> {emp.birth_date}</p>
               <p className="text-sm mb-1"><span className="text-slate-500 w-24 inline-block">E-mail:</span> {emp.email}</p>
               <p className="text-sm mb-1"><span className="text-slate-500 w-24 inline-block">Telefone:</span> {emp.phone}</p>
             </div>
             <div>
               <h3 className="font-semibold text-lg border-b pb-2 mb-4">Dados Profissionais</h3>
               <p className="text-sm mb-1"><span className="text-slate-500 w-28 inline-block">Admissão:</span> {emp.professional?.admission_date}</p>
               <p className="text-sm mb-1"><span className="text-slate-500 w-28 inline-block">Cargo:</span> {emp.professional?.role}</p>
               <p className="text-sm mb-1"><span className="text-slate-500 w-28 inline-block">Setor:</span> {emp.professional?.department}</p>
               <p className="text-sm mb-1"><span className="text-slate-500 w-28 inline-block">Salário Base:</span> {emp.professional?.base_salary}</p>
             </div>
             {emp.cnh?.has_cnh && (
                <div className="col-span-2 mt-4 bg-slate-50 p-4 rounded-lg">
                  <h3 className="font-semibold flex items-center gap-2 mb-2"><IdCard className="w-4 h-4"/> CNH (Motorista)</h3>
                  <div className="flex gap-8">
                    <p className="text-sm"><span className="text-slate-500">Nº:</span> {emp.cnh.number}</p>
                    <p className="text-sm"><span className="text-slate-500">Categoria:</span> {emp.cnh.category}</p>
                    <p className="text-sm"><span className="text-slate-500">Validade:</span> {emp.cnh.expiry_date}</p>
                  </div>
                </div>
             )}
           </div>
        )}

        {tab === 'docs' && (
          <div>
            <div className="flex justify-between mb-4">
              <h3 className="font-semibold text-lg">Documentos</h3>
              <Button size="sm" onClick={() => toast('Funcionalidade de upload a ser implementada pelo modal')}>Anexar Documento</Button>
            </div>
            <table className="w-full text-sm text-left">
              <thead className="bg-slate-50 text-slate-600 font-semibold border-b">
                <tr><th className="py-2 px-3">Nome</th><th className="py-2 px-3">Categoria</th><th className="py-2 px-3">Vencimento</th></tr>
              </thead>
              <tbody className="divide-y">
                {docs?.map(d => <tr key={d.id}><td className="py-2 px-3">{d.name}</td><td className="py-2 px-3">{d.category}</td><td className="py-2 px-3">{d.has_expiry ? d.expiry_date : '-'}</td></tr>)}
              </tbody>
            </table>
          </div>
        )}

        {tab === 'exams' && (
          <div>
            <div className="flex justify-between mb-4">
              <h3 className="font-semibold text-lg">Exames Ocupacionais</h3>
              <Button size="sm">Registrar Exame</Button>
            </div>
            <table className="w-full text-sm text-left"><thead className="bg-slate-50 border-b"><tr><th className="py-2 px-3">Tipo</th><th className="py-2 px-3">Data</th><th className="py-2 px-3">Validade</th><th className="py-2 px-3">Status</th></tr></thead><tbody className="divide-y">{exams?.map(e => <tr key={e.id}><td className="py-2 px-3">{e.type}</td><td className="py-2 px-3">{e.exam_date}</td><td className="py-2 px-3">{e.expiry_date}</td><td className="py-2 px-3">{e.status}</td></tr>)}</tbody></table>
          </div>
        )}

        {tab === 'vacations' && (
          <div>
            <div className="flex justify-between mb-4">
              <h3 className="font-semibold text-lg">Férias</h3>
              <Button size="sm">Programar Férias</Button>
            </div>
            <table className="w-full text-sm text-left"><thead className="bg-slate-50 border-b"><tr><th className="py-2 px-3">Período Aquisitivo</th><th className="py-2 px-3">Dias Usados</th><th className="py-2 px-3">Status</th></tr></thead><tbody className="divide-y">{vacations?.map(v => <tr key={v.id}><td className="py-2 px-3">{v.period}</td><td className="py-2 px-3">{v.used_days} / {v.available_days}</td><td className="py-2 px-3">{v.status}</td></tr>)}</tbody></table>
          </div>
        )}

        {tab === 'leaves' && (
          <div>
            <div className="flex justify-between mb-4">
              <h3 className="font-semibold text-lg">Afastamentos</h3>
              <Button size="sm">Registrar Afastamento</Button>
            </div>
            <table className="w-full text-sm text-left"><thead className="bg-slate-50 border-b"><tr><th className="py-2 px-3">Tipo</th><th className="py-2 px-3">Início</th><th className="py-2 px-3">Previsão Retorno</th><th className="py-2 px-3">Status</th></tr></thead><tbody className="divide-y">{leaves?.map(l => <tr key={l.id}><td className="py-2 px-3">{l.leave_type}</td><td className="py-2 px-3">{l.start_date}</td><td className="py-2 px-3">{l.expected_return}</td><td className="py-2 px-3">{l.status}</td></tr>)}</tbody></table>
          </div>
        )}

        {tab === 'occurrences' && (
          <div>
            <div className="flex justify-between mb-4">
              <h3 className="font-semibold text-lg">Ocorrências</h3>
              <Button size="sm">Nova Ocorrência</Button>
            </div>
            <table className="w-full text-sm text-left"><thead className="bg-slate-50 border-b"><tr><th className="py-2 px-3">Data</th><th className="py-2 px-3">Tipo</th><th className="py-2 px-3">Descrição</th></tr></thead><tbody className="divide-y">{occurrences?.map(o => <tr key={o.id}><td className="py-2 px-3">{o.date}</td><td className="py-2 px-3">{o.type}</td><td className="py-2 px-3">{o.description}</td></tr>)}</tbody></table>
          </div>
        )}

        {tab === 'timeline' && (
          <div className="max-w-2xl">
            <h3 className="font-semibold text-lg mb-6">Linha do Tempo (Auditoria)</h3>
            <div className="relative border-l-2 border-slate-200 ml-3 space-y-8">
              {timeline?.map((item, idx) => (
                <div key={idx} className="relative pl-6">
                  <div className="absolute w-3 h-3 bg-[#C52429] rounded-full -left-[7px] top-1.5" />
                  <p className="text-xs text-slate-500 font-mono mb-1">{item.date}</p>
                  <h4 className="font-semibold text-slate-800">{item.event}</h4>
                  <p className="text-sm text-slate-600 mt-1">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
